import { useCallback, useState } from 'react'
import { App, Modal } from 'antd'
import { useNavigate } from 'react-router-dom'
import { InterviewService } from '../services'
import { MindmapInterviewService } from '../services/mindmapInterviewService'

export interface UseInterviewInsightsActionsOptions {
  nodeId: string | undefined
  mindmapId: string | undefined
  parentLabel: string | null | undefined
  /** Called after simulate completes so the page can refetch notes. */
  onNotesChanged: () => void
}

export interface UseInterviewInsightsActionsResult {
  handleSimulate: () => Promise<void>
  handleDelete: () => void
  simulating: boolean
}

/**
 * Encapsulates the secondary actions on the interview insights page (simulate
 * a full interview and save it, delete) so the page component stays focused on
 * data loading and rendering.
 */
export function useInterviewInsightsActions({
  nodeId,
  mindmapId,
  parentLabel,
  onNotesChanged,
}: UseInterviewInsightsActionsOptions): UseInterviewInsightsActionsResult {
  const { notification } = App.useApp()
  const navigate = useNavigate()
  const [simulating, setSimulating] = useState(false)

  const handleSimulate = useCallback(async () => {
    if (!nodeId) return
    setSimulating(true)

    const simRes = await InterviewService.simulate(nodeId)
    if (simRes.error || !simRes.data) {
      setSimulating(false)
      notification.error({ title: 'Could not generate simulated answers. Please try again.' })
      return
    }

    const saveRes = await InterviewService.saveNotes(
      nodeId,
      simRes.data.answers,
      undefined,
      'Simulated Interview'
    )
    setSimulating(false)

    if (saveRes.error) {
      notification.error({ title: 'Simulated answers generated but could not be saved.' })
      return
    }

    notification.success({ title: 'Simulated interview submitted' })
    onNotesChanged()
  }, [nodeId, notification, onNotesChanged])

  const handleDelete = useCallback(() => {
    if (!mindmapId || !nodeId) return
    const label = parentLabel && parentLabel.length > 0 ? parentLabel : 'Untitled interview'
    Modal.confirm({
      title: 'Delete this interview?',
      content: `"${label}" and all of its recorded notes will be permanently deleted. This can't be undone.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        const res = await MindmapInterviewService.delete(mindmapId, nodeId)
        if (res.error) {
          notification.error({ title: 'Failed to delete interview' })
          return
        }
        notification.success({ title: 'Interview deleted' })
        void navigate(`/mindmap/${mindmapId}/research`, { replace: true })
      },
    })
  }, [mindmapId, nodeId, parentLabel, navigate, notification])

  return { handleSimulate, handleDelete, simulating }
}
